import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, Lock, Download, Lightning } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { wipeFinderTopRight } from "../lib/vaultQr";

/* ────────────────────────────────────────────────────────────────────
   /vault/new — generate a Curb Vault QR for one of the cardholder's
   cards. Mints a signed token from the backend, renders it with
   qrcode.react, then wipes the top-right finder pattern in a canvas
   pre-pass before letting the user download the PNG.

   PoC scope: requires the cardholder to be logged in. We pick the
   first card they own as the binding. A polished version would let
   them choose between cards and give the QR a friendly label.
   ──────────────────────────────────────────────────────────────────── */

export default function VaultQrNew() {
  const [cards, setCards] = useState([]);
  const [chId, setChId] = useState("");
  const [label, setLabel] = useState("Vault QR");
  const [token, setToken] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const hiddenQrRef = useRef(null);
  const displayCanvasRef = useRef(null);

  useEffect(() => {
    api.get("/cardholder/my-cards")
      .then((r) => {
        const items = r.data?.items || [];
        setCards(items);
        if (items[0]) setChId(items[0].id);
      })
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  async function mint() {
    if (!chId) return;
    setBusy(true);
    setErr("");
    setToken("");
    try {
      const r = await api.post("/cardholder/vault-qr/mint", { cardholder_id: chId, label });
      setToken(r.data.token);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  // Once qrcode.react has rendered the hidden QR canvas, copy it into the
  // visible canvas and wipe the top-right finder pattern. Re-runs every
  // time the token changes.
  useEffect(() => {
    if (!token) return;
    // qrcode.react renders into a child canvas of hiddenQrRef.
    const src = hiddenQrRef.current?.querySelector("canvas");
    const dest = displayCanvasRef.current;
    if (!src || !dest) return;
    const sz = src.width;
    dest.width = sz;
    dest.height = sz;
    const ctx = dest.getContext("2d");
    ctx.drawImage(src, 0, 0);
    const ok = wipeFinderTopRight(dest);
    if (!ok) setErr("Could not detect QR geometry to wipe finder. Try regenerating.");
  }, [token]);

  function downloadPng() {
    const dest = displayCanvasRef.current;
    if (!dest) return;
    const a = document.createElement("a");
    a.href = dest.toDataURL("image/png");
    a.download = `curb-vault-${Date.now()}.png`;
    a.click();
  }

  // Build the URL the QR will encode. The token already has signature.
  const scanUrl = token
    ? `${window.location.origin}/vault/scan#${encodeURIComponent(token)}`
    : "";

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-black text-white border-b border-black">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/cardholder" className="label-tag inline-flex items-center gap-2 text-[color:var(--cc-yellow)]" data-testid="vault-back-link">
            <ArrowLeft size={14} weight="bold" /> Back to dashboard
          </Link>
          <div className="label-tag inline-flex items-center gap-2">
            <Lock size={14} weight="bold" /> CURB VAULT QR · BETA
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="label-tag text-neutral-500">PROOF OF CONCEPT</div>
        <h1 className="font-display text-3xl sm:text-5xl mt-3 leading-tight">
          Mint a <span className="bg-[color:var(--cc-yellow)] px-1">Vault QR</span>.
        </h1>
        <p className="mt-3 text-sm text-neutral-700 max-w-xl leading-relaxed">
          One finder pattern is wiped before printing. Only the Curb Crew scanner
          page at <code className="font-mono-cc bg-neutral-100 px-1 py-0.5">/vault/scan</code> knows
          to reconstruct it. Standard phone cameras will fail silently.
        </p>

        <div className="mt-8 border-2 border-black p-6 bg-neutral-50">
          <label className="block">
            <div className="label-tag">CARD</div>
            <select
              className="mt-2 w-full border-2 border-black px-3 py-2 bg-white"
              value={chId}
              onChange={(e) => setChId(e.target.value)}
              data-testid="vault-card-select"
            >
              {cards.length === 0 && <option value="">(no active cards)</option>}
              {cards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.full_name || "Untitled"} · {c.card_type}
                </option>
              ))}
            </select>
          </label>

          <label className="block mt-4">
            <div className="label-tag">LABEL (your note · max 80 chars)</div>
            <input
              className="mt-2 w-full border-2 border-black px-3 py-2 bg-white"
              value={label}
              maxLength={80}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Passport vault · Safe deposit · Trade-secret store"
              data-testid="vault-label-input"
            />
          </label>

          <button
            onClick={mint}
            disabled={!chId || busy}
            className="mt-6 inline-flex items-center gap-2 bg-black text-[color:var(--cc-yellow)] px-5 py-3 label-tag disabled:opacity-50"
            data-testid="vault-mint-btn"
          >
            <Lightning size={14} weight="bold" /> {busy ? "Minting..." : "Mint Vault QR"}
          </button>

          {err && <div className="mt-4 p-3 bg-red-100 border-2 border-red-700 text-sm" data-testid="vault-error">{err}</div>}
        </div>

        {token && (
          <div className="mt-8 border-2 border-black p-6 bg-white">
            <div className="label-tag">YOUR VAULT QR</div>

            {/* Hidden canvas where qrcode.react draws the full intact QR.
                We grab the pixels from here and paint into displayCanvas. */}
            <div ref={hiddenQrRef} style={{ position: "absolute", left: -9999, top: -9999 }}>
              <QRCodeCanvas value={scanUrl} size={320} level="H" includeMargin />
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-6 items-center sm:items-start">
              <canvas
                ref={displayCanvasRef}
                className="border-2 border-black bg-white"
                style={{ width: 320, height: 320 }}
                data-testid="vault-qr-canvas"
              />
              <div className="text-sm space-y-3 flex-1">
                <div>
                  <div className="label-tag text-neutral-500">SCAN URL (encoded inside)</div>
                  <code className="block mt-1 text-xs bg-neutral-100 p-2 break-all font-mono-cc">
                    {scanUrl}
                  </code>
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed">
                  → Try scanning this with your phone camera. <strong>It will fail</strong> — the
                  top-right finder is missing. Now visit <code className="font-mono-cc">/vault/scan</code> on
                  Curb Crew and point it at the same image. <strong>It works</strong>.
                </p>
                <button
                  onClick={downloadPng}
                  className="inline-flex items-center gap-2 bg-[color:var(--cc-yellow)] text-black px-4 py-2 label-tag border border-[color:var(--cc-yellow)]"
                  data-testid="vault-download-btn"
                >
                  <Download size={14} weight="bold" /> Download PNG
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
